
import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { products } from '../../../mocks/products';

const formatPrice = (price: number) => price.toLocaleString('ko-KR');

export default function VehicleSearch() {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  // Build a map where the key is a vehicle name and the value is an array of products compatible with that vehicle
  const allVehicles = useMemo(() => {
    const vehicleMap = new Map<string, typeof products[number][]>();
    products.forEach((product) => {
      product.compatibleVehicles.forEach((vehicle) => {
        if (!vehicleMap.has(vehicle)) {
          vehicleMap.set(vehicle, []);
        }
        vehicleMap.get(vehicle)!.push(product);
      });
    });
    return vehicleMap;
  }, []);

  // Filter the vehicle map according to the search term
  const filteredVehicles = useMemo(() => {
    if (!searchTerm.trim()) return Array.from(allVehicles.entries());

    const term = searchTerm.toLowerCase();
    return Array.from(allVehicles.entries()).filter(([vehicle]) =>
      vehicle.toLowerCase().includes(term)
    );
  }, [searchTerm, allVehicles]);

  // List of all vehicle names, sorted alphabetically
  const vehicleList = useMemo(() => Array.from(allVehicles.keys()).sort(), [allVehicles]);

  return (
    <section className="py-12 md:py-20 bg-white">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="text-center mb-8 md:mb-12">
          <h2
            className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#3D2817] mb-3 md:mb-4"
            style={{ fontFamily: "'Anton', sans-serif" }}
          >
            호환 차종별 검색
          </h2>
          <p
            className="text-sm md:text-base text-[#6B5744]"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            차종을 검색하여 호환되는 부품을 찾아보세요
          </p>
        </div>

        {/* Search Input */}
        <div className="max-w-2xl mx-auto mb-8 md:mb-10">
          <div className="relative">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
              <i className="ri-search-line text-[#6B5744] text-lg"></i>
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setIsOpen(true);
              }}
              onFocus={() => setIsOpen(true)}
              placeholder="차종명을 입력하세요 (예: 다마스, 마티즈, 포터2...)"
              className="w-full pl-12 pr-4 py-3.5 md:py-4 bg-[#F5F1E8] border-2 border-[#3D2817]/20 rounded-lg text-sm md:text-base text-[#3D2817] placeholder-[#6B5744]/50 focus:outline-none focus:border-[#3D2817] transition-colors"
              style={{ fontFamily: "'Inter', sans-serif" }}
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => {
                  setSearchTerm('');
                  setIsOpen(false);
                }}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center cursor-pointer text-[#6B5744] hover:text-[#3D2817]"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            )}
          </div>

          {/* Quick Vehicle Tags */}
          {!searchTerm && !isOpen && (
            <div className="flex flex-wrap gap-2 mt-4">
              {vehicleList.slice(0, 12).map((vehicle) => (
                <button
                  key={vehicle}
                  type="button"
                  onClick={() => {
                    setSearchTerm(vehicle);
                    setIsOpen(true);
                  }}
                  className="px-3 py-1.5 bg-[#3D2817]/10 text-[#3D2817] text-xs md:text-sm rounded-full hover:bg-[#3D2817] hover:text-white transition-colors cursor-pointer whitespace-nowrap"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {vehicle}
                </button>
              ))}
              {vehicleList.length > 12 && (
                <button
                  type="button"
                  onClick={() => setIsOpen(true)}
                  className="px-3 py-1.5 bg-[#E63946]/10 text-[#E63946] text-xs md:text-sm rounded-full hover:bg-[#E63946] hover:text-white transition-colors cursor-pointer whitespace-nowrap"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  +{vehicleList.length - 12}개 더보기
                </button>
              )}
            </div>
          )}
        </div>

        {/* Search Results */}
        {isOpen && (
          <div className="max-w-5xl mx-auto">
            {filteredVehicles.length === 0 ? (
              <div className="text-center py-12 bg-[#F5F1E8] rounded-lg">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-search-line text-4xl text-[#6B5744]/40"></i>
                </div>
                <p
                  className="text-base text-[#6B5744]"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  &quot;{searchTerm}&quot;에 해당하는 차종을 찾을 수 없습니다
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {filteredVehicles.map(([vehicle, vehicleProducts]) => (
                  <div key={vehicle} className="bg-[#F5F1E8] rounded-lg p-4 md:p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center bg-[#3D2817] rounded-full">
                        <i className="ri-car-line text-white text-base md:text-lg"></i>
                      </div>
                      <h3
                        className="text-lg md:text-xl font-bold text-[#3D2817]"
                        style={{ fontFamily: "'Anton', sans-serif" }}
                      >
                        {vehicle}
                      </h3>
                      <span
                        className="text-xs md:text-sm text-[#6B5744] bg-white px-2 py-0.5 rounded-full"
                        style={{ fontFamily: "'Inter', sans-serif" }}
                      >
                        {vehicleProducts.length}개 부품
                      </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
                      {vehicleProducts.map((product) => (
                        <Link
                          key={product.id}
                          to={`/product/${product.id}`}
                          className="flex items-center gap-3 bg-white p-3 md:p-4 rounded-lg hover:shadow-md transition-shadow cursor-pointer group"
                          data-product-shop
                        >
                          <div className="w-16 h-16 md:w-20 md:h-20 flex-shrink-0 overflow-hidden rounded-md bg-gray-50">
                            <img
                              src={product.image}
                              alt={product.name}
                              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p
                              className="text-xs md:text-sm font-bold text-[#3D2817] truncate mb-1"
                              style={{ fontFamily: "'Inter', sans-serif" }}
                            >
                              {product.name}
                            </p>
                            <p
                              className="text-[10px] md:text-xs text-[#6B5744] truncate mb-1"
                              style={{ fontFamily: "'Inter', sans-serif" }}
                            >
                              {product.partNumber}
                            </p>
                            <p
                              className="text-sm md:text-base font-bold text-[#E63946]"
                              style={{ fontFamily: "'Inter', sans-serif" }}
                            >
                              ₩{formatPrice(product.price)}
                            </p>
                          </div>
                          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                            <i className="ri-arrow-right-s-line text-[#6B5744] group-hover:text-[#3D2817] transition-colors"></i>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Close Button */}
            {!searchTerm && (
              <div className="text-center mt-6">
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="px-6 py-2.5 text-sm text-[#6B5744] hover:text-[#3D2817] transition-colors cursor-pointer"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  접기
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
